import React, { useEffect } from 'react';
import { formatCurrency, currencySymbol } from '../../utils/formatters';

function formatMoney(n) {
  try {
    return formatCurrency(Number(n || 0));
  } catch (_) {
    return `${currencySymbol()}${Math.round(Number(n || 0)).toLocaleString()}`;
  }
}

const WealthGapSpark = ({ state = 'locked', today = 450000, goal = 1050000 }) => {
  const gap = Math.max(goal - today, 0);
  const pts = 14;
  const currentSeries = Array.from({ length: pts }, (_, i) => 0.45 + 0.18 * Math.sin((i / (pts - 1)) * Math.PI));
  const potentialSeries = Array.from({ length: pts }, (_, i) => 0.7 + 0.1 * Math.sin((i / (pts - 1)) * Math.PI));
  const W = 100, H = 52, padX = 4, baseY = H - 6;
  const x = (i) => padX + ((W - padX * 2) * i) / (pts - 1);
  const yFromRatio = (r) => baseY - r * (H - 18);
  const pathFrom = (arr) => arr.map((r, i) => `${i ? 'L' : 'M'} ${x(i)},${yFromRatio(r)}`).join(' ');
  const currentPath = pathFrom(currentSeries);
  const potentialPath = pathFrom(potentialSeries);
  const gapPoly = `${potentialSeries.map((r, i) => `${x(i)},${yFromRatio(r)}`).join(' ')} ${currentSeries.slice().reverse().map((r, j) => `${x(pts - 1 - j)},${yFromRatio(r)}`).join(' ')}`;
  const locked = state === 'locked';
  const badge = locked ? '🔒' : `Gap ${formatMoney(gap)}`;

  return (
    <div className="group w-full">
      <div className="flex items-center justify-between mb-2">
        <span className="text-lg font-medium text-succession-text-primary">How Big Is Your Wealth Gap?</span>
        <span className={`text-[10px] px-2 py-0.5 rounded-full ${locked ? 'bg-gray-100 text-gray-600' : 'bg-amber-50 text-amber-700'}`}>{badge}</span>
      </div>
      <div className={`relative h-24 ${locked ? 'opacity-70' : ''}`}>
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-full">
          <line x1="0" y1={baseY} x2={W} y2={baseY} stroke="currentColor" opacity="0.08" />
          <polygon points={gapPoly} fill="var(--wg-gap,#DC5A3F)" opacity="0.18" className="transition-opacity duration-300" />
          <path d={potentialPath} fill="none" stroke="var(--wg-target,#1F9D55)" strokeWidth="1.2" strokeDasharray="3 3" />
          <path d={`${currentPath} L ${W - padX},${baseY} L ${padX},${baseY} Z`} fill="var(--wg-today,#5B6B7A)" opacity="0.18" />
          <circle cx={x(pts - 2)} cy={yFromRatio(currentSeries[pts - 2])} r="1.6" fill="var(--wg-today,#5B6B7A)" />
          <circle cx={x(pts - 2)} cy={yFromRatio(potentialSeries[pts - 2])} r="1.6" fill="var(--wg-target,#1F9D55)" />
        </svg>
        {!locked && (
          <>
            <span className="absolute right-2 top-1 text-[10px] text-gray-500">Goal</span>
            <span className="absolute right-2 bottom-1 text-[10px] text-gray-500">You</span>
          </>
        )}
      </div>
    </div>
  );
};

const AssessmentCtaTile = ({ placement = 'dashboard_marketplace', state = 'locked' }) => {
  const onOpen = () => {
    try { if (window.ahoy?.track) window.ahoy.track('wealth_gap_tile_clicked', { source: placement }); } catch (_) {}
    window.location.href = '/succession/plans';
  };
  useEffect(() => { try { if (window.ahoy?.track) window.ahoy.track('wealth_gap_tile_viewed', { placement }); } catch (_) {} }, [placement]);

  return (
    <button onClick={onOpen} className="w-full text-left" aria-label="Open Wealth Gap">
      <div className="rounded-2xl border border-succession-border bg-white p-4 shadow-sm hover:shadow-md transition transform hover:scale-105 flex flex-col h-full">
        <WealthGapSpark state={state} />
        <div className="mt-3 flex items-center justify-between">
          <span className="text-xs text-succession-text-secondary">{state === 'locked' ? 'Take the checkup 🔒' : 'Tap to view your report'}</span>
          <span className="text-xs font-medium text-succession-primary group-hover:underline">Open</span>
        </div>
      </div>
    </button>
  );
};

export default AssessmentCtaTile;


